import { useQueryClient } from '@tanstack/react-query';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type UseEvacZonesChangedNotificationProps = {
  geoEventId?: number;
};

export type UseEvacZonesChangedNotification = {
  onZoneStateChanged: () => void;
  lockEvacuationZonesChangedNotification: () => void;
  unlockEvacuationZonesChangedNotification: () => void;
};

export const useEvacZonesChangedNotification = ({
  geoEventId,
}: UseEvacZonesChangedNotificationProps): UseEvacZonesChangedNotification => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // close the zonesChangedSnackbar when the component unmounts
  const zonesChangedSnackbar = useRef<SnackbarKey>();
  useEffect(() => {
    return () => {
      if (zonesChangedSnackbar.current) {
        closeSnackbar(zonesChangedSnackbar.current);
        zonesChangedSnackbar.current = undefined;
      }
    };
  }, []);

  const notifyEvacuationZonesChanged = useRef(true);
  const onZoneStateChanged = (): void => {
    if (!notifyEvacuationZonesChanged.current) {
      return;
    }
    zonesChangedSnackbar.current = enqueueSnackbar(
      t('geoEventEvacuations.evacZones.zonesUpdated'),
      {
        variant: 'warning',
        persist: true,
        preventDuplicate: true,
      },
    );
    if (geoEventId) {
      // ensure geoevent is refetched to get the latest evac zones statuses
      queryClient.invalidateQueries({
        queryKey: ['geoEvent', geoEventId],
      });
    }
  };

  return {
    onZoneStateChanged,
    lockEvacuationZonesChangedNotification: () => {
      notifyEvacuationZonesChanged.current = false;
    },
    unlockEvacuationZonesChangedNotification: () => {
      notifyEvacuationZonesChanged.current = true;
    },
  };
};
